import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ArrowRightLeft, Clock, Users, Layers, Sun } from "lucide-react";
import {
  Table as TableType,
  CombinedTable,
  Reservation,
} from "@/types/table";
import { formatCustomerName } from "@/lib/clientUtils";
import { useServiceTimeCompare } from "@/hooks/useServiceTimeCompare";

interface SwapPickerSourceReservation {
  id: number;
  supabaseId?: string;
  name: string;
  persons: number;
  time: string;
  reservationDate: Date;
  tableId?: string;
  combinedTableId?: string;
}

interface SwapReservationPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: SwapPickerSourceReservation;
  allReservations: Reservation[];
  tables: TableType[];
  combinedTables: CombinedTable[];
  /** Called when user picks a candidate. Returns to caller which then opens the confirm dialog. */
  onPick: (other: Reservation) => void;
}

const HHMM_TO_MIN = (hhmm: string): number => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Picker dialog listing other active reservations of the same date that have a
 * table assignment. Defaults to ±2h of the source's time; toggle "Tout afficher"
 * shows the whole day.
 */
export const SwapReservationPickerDialog = ({
  open,
  onOpenChange,
  source,
  allReservations,
  tables,
  combinedTables,
  onPick,
}: SwapReservationPickerDialogProps) => {
  const compareServiceTime = useServiceTimeCompare();
  const [showAll, setShowAll] = useState(false);

  const sourceMinutes = HHMM_TO_MIN(source.time);

  const candidates = useMemo(() => {
    return allReservations
      .filter((r) => {
        if (r.status !== "planifié" && r.status !== "arrivé") return false;
        if (!r.tableId && !r.combinedTableId) return false;
        const rId = r.supabaseId ?? String(r.id);
        const srcId = source.supabaseId ?? String(source.id);
        if (rId === srcId) return false;
        const rDate = r.date instanceof Date ? r.date : new Date(r.date);
        if (!sameDay(rDate, source.reservationDate)) return false;
        if (!showAll) {
          const diff = Math.abs(HHMM_TO_MIN(r.startTime) - sourceMinutes);
          if (diff > 120) return false;
        }
        return true;
      })
      .sort((a, b) => compareServiceTime(a.startTime, b.startTime));
  }, [allReservations, source, sourceMinutes, showAll, compareServiceTime]);

  const getTableLabel = (r: Reservation) => {
    if (r.tableId) {
      const t = tables.find((t) => t.id === r.tableId);
      return { name: t?.name ?? "Table ?", isTerrace: t?.isTerrace, isCombined: false };
    }
    if (r.combinedTableId) {
      const c = combinedTables.find((c) => c.id === r.combinedTableId);
      return { name: c?.name ?? "Combinée ?", isTerrace: c?.isTerrace, isCombined: true };
    }
    return { name: "—", isTerrace: false, isCombined: false };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-base flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" />
            Échanger la table avec…
          </DialogTitle>
          <DialogDescription className="text-xs">
            Sélectionnez la réservation avec qui échanger la table de{" "}
            <strong>{source.name}</strong> ({source.time}).
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between px-4 py-2 border-y bg-muted/30">
          <span className="text-xs text-muted-foreground">
            {showAll ? "Toute la journée" : "± 2h du créneau"} · {candidates.length} résultat
            {candidates.length > 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs">Tout afficher</span>
            <Switch checked={showAll} onCheckedChange={setShowAll} />
          </div>
        </div>

        <div className="max-h-[360px] overflow-y-auto divide-y">
          {candidates.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground space-y-3">
              <p>Aucune autre réservation à échanger {showAll ? "ce jour" : "dans ce créneau"}.</p>
              {!showAll && (
                <Button variant="outline" size="sm" onClick={() => setShowAll(true)}>
                  Voir toute la journée
                </Button>
              )}
            </div>
          )}
          {candidates.map((r) => {
            const label = getTableLabel(r);
            const customerName =
              formatCustomerName(r.customerFirstName, r.customerLastName) || "Client";
            return (
              <button
                key={r.supabaseId ?? r.id}
                onClick={() => onPick(r)}
                className="w-full text-left px-4 py-2.5 hover:bg-accent transition-colors flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium">{r.startTime.substring(0, 5)}</span>
                    <span className="text-sm truncate">{customerName}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                    <Users className="w-3 h-3" /> {r.numberOfPeople}
                    <span>·</span>
                    {label.isCombined && <Layers className="w-3 h-3" />}
                    {label.isTerrace && <Sun className="w-3 h-3 text-amber-500" />}
                    <span>{label.name}</span>
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px]">
                  Choisir
                </Badge>
              </button>
            );
          })}
        </div>

        <div className="px-4 py-3 border-t flex justify-end">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
